// frontend/src/pages/GestionLogin.jsx
// Page de connexion admin avec 2FA Google Authenticator
// 73+ Papy - Décembre 2025

import React, { useState } from 'react';
import { Shield, Eye, EyeOff, AlertCircle, Loader, Smartphone, CheckCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const GestionLogin = () => {
  // États connexion
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // États 2FA
  const [step, setStep] = useState('login'); // 'login' ou 'totp'
  const [totpCode, setTotpCode] = useState('');
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingToken, setPendingToken] = useState(null);

  // Étape 1 : Login + Password
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!login.trim()) {
      setError('Identifiant requis');
      return;
    }
    if (!password) {
      setError('Mot de passe requis');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/auth/admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login: login.trim(),
          password: password
        })
      });

      const data = await response.json();

      if (data.success) {
        // Vérifier si 2FA est activé pour cet utilisateur
        const totpStatus = await fetch(`${API_BASE}/totp/status/${data.user.id}`);
        const totpData = await totpStatus.json();

        if (totpData.success && totpData.enabled) {
          // 2FA activé → demander le code
          setPendingUser(data.user);
          setPendingToken(data.token);
          setStep('totp');
          setTotpCode('');
        } else {
          // Pas de 2FA → connexion directe
          completeLogin(data.token, data.user);
        }
      } else {
        setError(data.error || 'Identifiants incorrects');
      }
    } catch (err) {
      console.error('Erreur connexion:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Étape 2 : Vérification code TOTP
  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!totpCode || totpCode.length !== 6) {
      setError('Code à 6 chiffres requis');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/totp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: pendingUser.id,
          token: totpCode
        })
      });

      const data = await response.json();

      if (data.success) {
        // Code valide → finaliser la connexion
        completeLogin(pendingToken, pendingUser);
      } else {
        setError(data.error || 'Code incorrect');
        setTotpCode('');
      }
    } catch (err) {
      console.error('Erreur 2FA:', err);
      setError('Erreur de vérification');
    } finally {
      setLoading(false);
    }
  };

  // Finaliser la connexion
  const completeLogin = (token, user) => {
    localStorage.setItem('farm_admin_token', token);
    localStorage.setItem('farm_admin_user', JSON.stringify(user));
    console.log('✅ Connexion admin réussie:', user.login);
    window.location.href = '/admin';
  };

  // Retour à l'étape login
  const backToLogin = () => {
    setStep('login');
    setTotpCode('');
    setPendingUser(null);
    setPendingToken(null);
    setError('');
  };

  // Auto-focus et auto-submit pour le code TOTP
  const handleTotpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setTotpCode(value);
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-sm w-full">
        
        {/* Logo/Icon */}
        <div className="text-center mb-8">
          <div className="bg-slate-700 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            {step === 'login' ? (
              <Shield className="h-10 w-10 text-slate-300" />
            ) : (
              <Smartphone className="h-10 w-10 text-green-400" />
            )}
          </div>
          <h1 className="text-xl font-semibold text-slate-300">
            {step === 'login' ? 'Espace Gestion' : 'Vérification 2FA'}
          </h1>
          {step === 'totp' && (
            <p className="text-slate-400 text-sm mt-2">
              Entrez le code de Google Authenticator
            </p>
          )}
        </div>

        {/* Formulaire Login */}
        {step === 'login' && (
          <div className="bg-white rounded-xl shadow-2xl p-6">
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              
              {/* Identifiant */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Identifiant
                </label>
                <input
                  type="text"
                  value={login}
                  onChange={(e) => {
                    setLogin(e.target.value);
                    setError('');
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                  placeholder="Votre identifiant"
                  autoFocus
                  autoComplete="username"
                  disabled={loading}
                />
              </div>

              {/* Mot de passe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Erreur */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Bouton */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-800 text-white py-3 rounded-lg hover:bg-slate-900 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>

            {/* Lien retour */}
            <div className="mt-6 text-center">
              <a 
                href="/"
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                ← Retour à l'accueil
              </a>
            </div>
          </div>
        )}

        {/* Formulaire TOTP */}
        {step === 'totp' && (
          <div className="bg-white rounded-xl shadow-2xl p-6">
            <form onSubmit={handleTotpSubmit} className="space-y-4">
              
              {/* Info utilisateur */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <p className="text-sm text-green-700">
                  Connecté en tant que <strong>{pendingUser?.login}</strong>
                </p>
              </div>

              {/* Code TOTP */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code à 6 chiffres
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={totpCode}
                  onChange={handleTotpChange}
                  className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-center text-2xl font-mono tracking-widest"
                  placeholder="000000"
                  autoFocus
                  maxLength={6}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Ouvrez Google Authenticator sur votre téléphone
                </p>
              </div>

              {/* Erreur */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Boutons */}
              <button
                type="submit"
                disabled={loading || totpCode.length !== 6}
                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  'Valider'
                )}
              </button>

              <button
                type="button"
                onClick={backToLogin}
                className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                ← Retour
              </button>
            </form>
          </div>
        )}

        {/* Footer discret */}
        <p className="text-center text-slate-500 text-xs mt-6">
          Accès réservé au personnel
        </p>
      </div>
    </div>
  );
};

export default GestionLogin;